﻿import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid2 from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import { createStyles, makeStyles } from "@mui/styles";
import AddBoxIcon from "@mui/icons-material/AddBox";
import DeleteIcon from "@mui/icons-material/Delete";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	IContextMenuParams,
	MenuItemDef,
	RowClassParams,
	RowNode,
	SelectionChangedEvent,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DrawingsDownloadButtons } from "./components/download-buttons/drawings-download-buttons.columns";
import { DocumentNameColumn } from "./components/editing-row/document-name.column";
import { DrawingVersionColumn } from "./components/editing-row/drawing-Version.column";
import { DrawingDateTimeColumn } from "./components/editing-row/drawing-dateTime.column";
import { DrawingTitleColumn } from "./components/editing-row/drawing-title.column";
import { ErrorModal } from "@dexteel/mesf-core";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import CustomLoadingOverlay from "../../../../../../components/loaders/LazyLoading/LazyLoading";
import { useWorkOrderModalsContext } from "../../../../context/work-order-modals.context";
import { useWorkOrderDrawingsActionsAndButtons } from "./hooks/useWorkOrdertDrawingsGridActionsAndButtons";
import { useSearchDrawings } from "./hooks/useSearchDrawings";
import { useSearchWorkOrderDrawings } from "./hooks/useSearchWorkOrderDrawings";
import { DrawingViewer } from "./components/viewer/drawing-viewer";
import { AssetAddDrawingsDialog } from "../asset-resources-dialogs/drawings-resource/asset-add-drawings-dialog";
import { DrawingPicker } from "./components/linker-drawings/drawings-picker";

const useStyles = makeStyles((theme) =>
	createStyles({
		agGridContainer: {
			position: "relative",
		},
		btnAdd: {
			position: "absolute",
			padding: 0,
			bottom: 5.5,
			right: 25,
		},
		btnDelete: {
			position: "absolute",
			padding: 0,
			bottom: 7,
			right: 63,
		},
		alertWarning: {
			position: "absolute",
			padding: "0 10px",
			bottom: 5.5,
			left: 50,
		},
	}),
);

const VITE_DOCUMENT_PATH =
	import.meta.env.VITE_DOCUMENT_PATH ||
	"https://mill-cmms.roanokesteel.com/vaultix/";

type Props = {
	WorkOrderId: number | null;
	newMode?: boolean;
	isLoadingResourcesList?: boolean;
	AssetId: number | null;
	searchAssetResources: Function;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	setShowAssetDrawingsPicker: Function;
	WorkOrderName: string;
	AssetName: string;
	Title?: string;
};

export const WorkOrderDrawingsTable = ({
	WorkOrderId,
	newMode,
	AssetId,
	searchAssetResources,
	show,
	isLoadingResourcesList,
	WorkOrderName,
	AssetName,
}: Props) => {
	const classes = useStyles();
	const [openViewer, setOpenViewer] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");
	const [errorApi, setErrorApi] = useState("");

	const [isWorkOrderDrawingsLoading, setIsWorkOrderDrawingsLoading] =
		useState<boolean>(false);
	const [workOrderDrawingsError, setWorkOrderDrawingsError] =
		useState<string>("");

	const [linkDrawingError, setLinkDrawingError] = useState<string>("");
	const [removeDrawingError, setRemoveDrawingError] = useState<string>("");

	const [showLinkResourcesToAssetDialog, setShowLinkResourcesToAssetDialog] =
		useState<boolean>(false);

	const [localShowInheritMessage, setLocalShowInheritMessage] = useState(false);
	const [openDrawingsPicker, setOpenDrawingsPicker] = useState(false);

	const [listMessage, setListMessage] = useState<string>("");
	const {
		state: { assetDrawings, workOrderDrawingsList, rowSelectedInDrawing },
		actions: {
			setDrawingSearch,
			setRowSelectedInDrawing,
			setDocumentIdRowSelected,
		},
	} = useWorkOrderModalsContext();

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const containerStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const [listDrawingSelected, setListDrawingSelected] = useState<any[]>([]);
	const [columnDefs, setColumnDefs] = useState<ColDef[]>();

	const searchWorkOrderDrawings = useSearchWorkOrderDrawings({
		WorkOrderId,
		setIsWorkOrderDrawingsLoading,
		setWorkOrderDrawingsError,
	});

	// SEARCH DRAWINGS
	const searchDrawings = useSearchDrawings({ setIsLoading, setError });

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		const contextMenuParams: IContextMenuParams = {
			rowNode: params.node,
			column: params.column,
			value: params.node?.data,
			x: e.nativeEvent.clientX,
			y: e.nativeEvent.clientY,
			source: "ui",
		};
		params.api.showContextMenu(contextMenuParams);
	};

	const getRowStyle = useCallback(
		(params: RowClassParams): { [key: string]: string } | undefined => {
			if (params.data.IsAssetResource) {
				return { backgroundColor: "#e8f5e9" };
			}
		},
		[],
	);

	const {
		refresh,
		addRow,
		removeDrawings,
		addButton,
		deleteButton,
		saveButton,
		discardButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	} = useWorkOrderDrawingsActionsAndButtons({
		WorkOrderId,
		newMode,
		setShowLinkResourcesToAssetDialog,
		gridRef: gridApi,
		searchWorkOrderDrawings,
		setRemoveDrawingError,
		setListMessage,
	});

	const renderColumns = () => {
		setColumnDefs([]);
		const newColumns: ColDef[] = [];
		newColumns.push({
			field: "",
			flex: 1,
			minWidth: 33,
			maxWidth: 33,
			filter: false,
			headerCheckboxSelection: true,
			checkboxSelection: true,
			headerCheckboxSelectionFilteredOnly: true,
		});
		newColumns.push({
			cellRenderer: DocumentNameColumn,
			type: "columnProperties",
			field: "Document",
			flex: 2,
			minWidth: 170,
			maxWidth: 170,
		});
		newColumns.push({
			cellRenderer: DrawingTitleColumn,
			type: "columnProperties",
			field: "Title",
			headerName: "Title",
			flex: 4,
			minWidth: 290,
		});
		newColumns.push({
			cellRenderer: DrawingDateTimeColumn,
			type: "columnProperties",
			valueFormatter: (params) => params.data.CreationDateTime?.slice(0, 10),
			field: "CreationDatetime",
			headerName: "Datetime",
			flex: 1,
			minWidth: 80,
			maxWidth: 80,
		});
		newColumns.push({
			cellRenderer: DrawingVersionColumn,
			type: "columnProperties",
			field: "Version",
			headerName: "Version",
			flex: 1,
			minWidth: 50,
			maxWidth: 50,
		});
		newColumns.push({
			cellRenderer: DrawingsDownloadButtons,
			field: "Download",
			flex: 2,
			minWidth: 250,
			editable: false,
			autoHeight: true,
			suppressNavigable: true,
		});
		newColumns.push({
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid2
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid2
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
							size={{ xs: 12, md: 12 }}
						>
							<Grid2
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid2 size={{ xs: 12, md: 12 }}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}
									>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				);
			},
		});
		setColumnDefs(newColumns);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			setRowSelectedInDrawing(params.node?.data);
			const rowSelectedInDrawingInTable = params.node?.data;

			const DownloadOriginal = () => {
				const link = document.createElement("a");
				link.href = `${VITE_DOCUMENT_PATH}${params.node?.data.DocPath}`;
				link.setAttribute("target", `_blank}`);
				link.setAttribute(
					"download",
					`${params.node?.data.Title} ${params.node?.data.DocPath}`,
				);
				document.body.appendChild(link);
				link.click();
			};

			const DownloadPDF = () => {
				const link = document.createElement("a");
				link.href = `${VITE_DOCUMENT_PATH}${params.node?.data.PDFPath}`;
				link.setAttribute("target", `_blank}`);
				link.setAttribute(
					"download",
					`${params.node?.data.Title} ${params.node?.data?.PDFPath}`,
				);
				document.body.appendChild(link);
				link.click();
			};

			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (
				rowSelectedInDrawingInTable &&
				rowSelectedInDrawingInTable?.id !== -1
			) {
				result.push({
					name: "Document Properties</strong>",
					action: () => {
						setOpenViewer(!openViewer);
						setRowSelectedInDrawing(params.node?.data);
						setDocumentIdRowSelected(rowSelectedInDrawingInTable.DocumentId);
					},
					icon: '<i class="fas fa-file-invoice"></i>',
				});
			}

			if (params.node?.data) {
				result.push({
					name: "Remove Document",
					action: () => {
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			if (rowSelectedInDrawingInTable) {
				if (params.node?.data.PDFPath) {
					result.push({
						name: "Download PDF",
						action: DownloadPDF,
						icon: '<i class="far fa-file-pdf"></i>',
					});
				}
				if (params.node?.data.DocPath) {
					result.push({
						name: "Download original",
						action: DownloadOriginal,
						icon: '<i class="far fa-file-alt"></i>',
					});
				}
			}

			if (rowSelectedInDrawingInTable) {
				result.push("separator");
			}

			if (!saveButton) {
				result.push({
					name: "Add Document Link",
					action: () => setOpenDrawingsPicker(true),
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			return result;
		},
		[
			deleteButton,
			saveButton,
			addRow,
			WorkOrderId,
			gridApi,
			rowSelectedInDrawing,
		],
	);

	const onSelectionChanged = useCallback(
		(event: SelectionChangedEvent) => {
			const selectedRows = event.api.getSelectedRows();
			setListDrawingSelected(selectedRows);
		},
		[setListDrawingSelected],
	);

	const openModalViewerActive = () => {
		const selectedRow: RowNode<any>[] | any = gridApi?.getSelectedNodes();
		if (selectedRow[0]!.data !== undefined) {
			setRowSelectedInDrawing(selectedRow[0]!.data);
			setOpenViewer(!openViewer);
		}
	};

	useEffect(() => {
		setSaveButton(false);
		setDiscardButton(false);
		setAddButton(true);
		setDeleteButton(false);
		setDeleteOpen(false);
		setDrawingSearch("");
	}, [WorkOrderId]);

	useEffect(() => {
		renderColumns();
	}, []);

	useEffect(() => {
		const hasAssetResource = workOrderDrawingsList.some(
			(drawing) => drawing.IsAssetResource === true,
		);
		const shouldShowInheritMessage =
			(!hasAssetResource || workOrderDrawingsList.length === 0) &&
			assetDrawings.length > 0;

		if (show && newMode && shouldShowInheritMessage) {
			setLocalShowInheritMessage(true);
		} else if (!show) {
			setLocalShowInheritMessage(false);
		}
	}, [assetDrawings]);

	return (
		<>
			{show && (
				<div style={containerStyle}>
					<Grid2 container style={gridStyle} className="ag-theme-alpine">
						<Grid2
							style={{ paddingBottom: 45 }}
							className={classes.agGridContainer}
							size={{ md: 12, xs: 12 }}
						>
							<AgGridReact
								onGridReady={(params) => setGridApi(params.api)}
								rowData={workOrderDrawingsList}
								columnDefs={columnDefs}
								headerHeight={32}
								rowSelection={"multiple"}
								animateRows={true}
								getContextMenuItems={getContextMenuItems}
								onSelectionChanged={onSelectionChanged}
								loadingOverlayComponent={loadingOverlayComponent}
								onRowDoubleClicked={openModalViewerActive}
								getRowStyle={getRowStyle}
								overlayNoRowsTemplate={"No drawings added"}
							/>
							{addButton && (
								<>
									<IconButton
										onClick={() => setOpenDrawingsPicker(true)}
										aria-label="add"
										color="primary"
										className={classes.btnAdd}
									>
										<AddBoxIcon style={{ width: 35, height: 35 }} />
									</IconButton>
								</>
							)}
							{deleteButton && (
								<>
									<IconButton
										onClick={() => setDeleteOpen(true)}
										aria-label="delete"
										color="secondary"
										className={classes.btnDelete}
										style={discardButton ? { right: 113 } : { right: 63 }}
									>
										<DeleteIcon style={{ width: 35, height: 35 }} />
									</IconButton>
								</>
							)}
						</Grid2>
						<Dialog open={deleteOpen} aria-labelledby="responsive-dialog-title">
							<DialogTitle id="responsive-dialog-title">
								{"WARNING"}
							</DialogTitle>
							<DialogContent>
								<DialogContentText style={{ padding: 0, margin: 0 }}>
									{listDrawingSelected.length > 0 ? (
										listDrawingSelected.length === 1 ? (
											<p style={{ margin: "0" }}>
												Remove <b>{listDrawingSelected[0]?.Title}</b> from work
												order?
											</p>
										) : (
											<>
												<p style={{ margin: 0 }}> Remove:</p>
												{listDrawingSelected.map((Drawing) => (
													<p key={Drawing.Title} style={{ margin: "5px 0" }}>
														<b>⁃ {Drawing.Title}, </b>
													</p>
												))}
												from work order?
											</>
										)
									) : rowSelectedInDrawing ? (
										<p style={{ margin: "0" }}>
											Remove <b>{rowSelectedInDrawing?.Title}</b> from work
											order?
										</p>
									) : null}
								</DialogContentText>
							</DialogContent>
							<DialogActions style={{ padding: "16px 24px" }}>
								<Button onClick={() => setDeleteOpen(false)} color="inherit">
									CANCEL
								</Button>
								<Button
									color="secondary"
									onClick={() => {
										if (listDrawingSelected.length > 0) {
											removeDrawings(listDrawingSelected);
										} else if (rowSelectedInDrawing) {
											removeDrawings([rowSelectedInDrawing]);
										}
									}}
								>
									REMOVE
								</Button>
							</DialogActions>
						</Dialog>
						<AssetAddDrawingsDialog
							show={showLinkResourcesToAssetDialog}
							onHide={(shouldUpdate: boolean) => {
								setShowLinkResourcesToAssetDialog(false);
								if (shouldUpdate) {
									searchWorkOrderDrawings();
								}
							}}
							AssetId={AssetId as number}
							AssetName={AssetName as string}
							WorkOrderName={WorkOrderName as string}
							searchAssetResources={searchAssetResources}
						/>
						<DrawingPicker
							searchDrawings={searchDrawings}
							show={openDrawingsPicker}
							onHide={(shouldShow: boolean) => {
								if (shouldShow) {
									searchWorkOrderDrawings();
									setOpenDrawingsPicker(false);
								}
								setOpenDrawingsPicker(false);
							}}
							WorkOrderId={WorkOrderId as number}
							isLoading={isLoading}
							setShowLinkResourcesToAssetDialog={
								setShowLinkResourcesToAssetDialog
							}
						/>
						<DrawingViewer
							show={openViewer}
							onHide={(shouldShow: boolean) => {
								setOpenViewer(false);
							}}
						/>
					</Grid2>
					<ErrorModal error={error} onHide={() => setError("")} />
					<ErrorModal error={errorApi} onHide={() => setErrorApi("")} />
					<ErrorModal
						error={workOrderDrawingsError}
						onHide={() => setWorkOrderDrawingsError("")}
					/>
					<ErrorModal
						error={linkDrawingError}
						onHide={() => setLinkDrawingError("")}
					/>
					<ErrorModal
						error={removeDrawingError}
						onHide={() => setRemoveDrawingError("")}
					/>
					<ErrorModal error={listMessage} onHide={() => setListMessage("")} />
				</div>
			)}
		</>
	);
};
